import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {ColorPicker as Picker, useColor} from "react-color-palette";
import "react-color-palette/lib/css/styles.css";
import Button from "../Button/Button";

type ColorPickerProps = {
    className?: string,
    changeHandler: any,
    currentColor: any,
}

const POPUP_WIDTH = 260;
const POPUP_GAP = 8;
const VIEWPORT_MARGIN = 8;

/** Read the current app theme from <body> class. Decouples this Pure-UI component from ThemeContext. */
const readBodyTheme = (): 'dark' | 'light' =>
    typeof document !== 'undefined' && document.body.classList.contains('dark-theme') ? 'dark' : 'light';

const ColorPicker = (props: ColorPickerProps) => {
    const {className, changeHandler, currentColor} = props;
    const [color, setColor] = useColor('hex', currentColor);
    const [showPicker, setShowPicker] = useState(false);
    const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);
    const [theme, setTheme] = useState<'dark' | 'light'>(readBodyTheme);

    const blockRef = useRef<HTMLDivElement | null>(null);
    const popupRef = useRef<HTMLDivElement | null>(null);

    // ----- Track app theme so the inner Picker switches between its `light` and `dark` skins. -----
    useEffect(() => {
        const update = () => setTheme(readBodyTheme());
        update();
        const observer = new MutationObserver(update);
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    /**
     * Compute popup position so it sits to the LEFT of the swatch and grows DOWNWARD from the swatch's top edge.
     * This keeps the popup inside the User Settings modal (swatch is near the modal's top-right, so there is
     * plenty of room left + down, and almost none up + right). Falls back to right-of-swatch if no room on the left.
     */
    const updatePopupPosition = () => {
        const block = blockRef.current;
        if (!block) return;
        const blockRect = block.getBoundingClientRect();
        const popupHeight = popupRef.current?.offsetHeight ?? 280;
        const popupWidth = popupRef.current?.offsetWidth ?? POPUP_WIDTH;

        // Default: open down-and-left of the swatch.
        let left = blockRect.left - POPUP_GAP - popupWidth;
        let top = blockRect.top;

        // If there's no room on the left, flip to the right of the swatch.
        if (left < VIEWPORT_MARGIN) {
            left = blockRect.right + POPUP_GAP;
        }
        // Final horizontal clamp inside viewport.
        const maxLeft = window.innerWidth - popupWidth - VIEWPORT_MARGIN;
        if (left > maxLeft) left = Math.max(VIEWPORT_MARGIN, maxLeft);

        // If the popup would overflow the bottom of the viewport, shift it up just enough to fit.
        const maxTop = window.innerHeight - popupHeight - VIEWPORT_MARGIN;
        if (top > maxTop) top = maxTop;
        if (top < VIEWPORT_MARGIN) top = VIEWPORT_MARGIN;

        setPopupPos({ top, left });
    };

    const handlePicker = () => setShowPicker(prev => !prev);
    const selectColorHandler = () => changeHandler(color.hex);

    // ----- Outside-click & repositioning when picker is open -----
    useLayoutEffect(() => {
        if (!showPicker) return;
        updatePopupPosition();
    }, [showPicker]);

    useEffect(() => {
        if (!showPicker) return;

        const handleDocumentMouseDown = (event: MouseEvent) => {
            const target = event.target as Node | null;
            if (!target) return;
            if (popupRef.current?.contains(target)) return;
            if (blockRef.current?.contains(target)) return;
            setShowPicker(false);
        };
        const handleReflow = () => updatePopupPosition();

        document.addEventListener('mousedown', handleDocumentMouseDown);
        window.addEventListener('resize', handleReflow);
        window.addEventListener('scroll', handleReflow, true);

        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDown);
            window.removeEventListener('resize', handleReflow);
            window.removeEventListener('scroll', handleReflow, true);
        };
    }, [showPicker]);

    /** Popup rendered into document.body so the modal's overflow contexts cannot clip it. */
    const popup = showPicker
        ? createPortal(
            <div
                ref={popupRef}
                className={`color__picker--portal ${className ?? ''}`.trim()}
                style={{
                    position: 'fixed',
                    top: popupPos?.top ?? -9999,
                    left: popupPos?.left ?? -9999,
                    width: POPUP_WIDTH,
                    visibility: popupPos ? 'visible' : 'hidden',
                }}
            >
                <Picker
                    width={POPUP_WIDTH - 20}
                    height={100}
                    color={color}
                    onChange={setColor}
                    hideHSV
                    dark={theme === 'dark'}
                    alpha
                />
                <Button
                    variant='btn-primary'
                    size='btn-md'
                    onClick={selectColorHandler}
                    style={{width: "100%"}}
                >
                    Select
                </Button>
            </div>,
            document.body,
        )
        : null;

    return (
        <div className='color-picker-wrapper'>
            {popup}
            <div
                ref={blockRef}
                className='color__block'
                style={{
                    backgroundColor: currentColor,
                    border: "1px solid var(--border)"
                }}
                onClick={handlePicker}
            />
        </div>
    );
}

export default ColorPicker;
